import React, { useState, useEffect } from 'react';
import { Mail, Loader2 } from 'lucide-react';
import { Button, useToast } from '../../components';
import { orderService } from '../../services';

interface SendMailButtonProps {
  orderId: string;
  isDarkMode: boolean;
  onSuccess?: () => void;
}

const SendMailButton: React.FC<SendMailButtonProps> = ({ orderId, isDarkMode, onSuccess }) => {
  const toast = useToast();
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  
  // Track if email was sent in this session/localStorage
  const [sent, setSent] = useState(() => {
    const sentEmails = JSON.parse(localStorage.getItem('sentEmails') || '{}');
    return !!sentEmails[orderId];
  });

  const handleSendMail = async (force: boolean = false) => {
    if (sent && !force) {
      setShowConfirm(true);
      return;
    }

    try {
      setSending(true);
      const response = await orderService.resendConfirmation(orderId);
      
      if (response.success) {
        toast.success('Email Sent', `Order confirmation email sent for order #${orderId}`);
        const sentEmails = JSON.parse(localStorage.getItem('sentEmails') || '{}');
        sentEmails[orderId] = true;
        localStorage.setItem('sentEmails', JSON.stringify(sentEmails));
        setSent(true);
        setShowConfirm(false);
        if (onSuccess) onSuccess();
      } else {
        toast.error('Failed to Send', response.message || 'Could not send email');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend confirmation email';
      toast.error('Error', errorMessage);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="small"
        onClick={() => handleSendMail(false)}
        disabled={sending}
        className={`flex items-center gap-2 transition-all duration-300 ${
          sent 
            ? (isDarkMode ? 'border-green-500/50 text-green-400 hover:bg-green-500/10' : 'border-green-200 text-green-600 hover:bg-green-50')
            : (isDarkMode ? 'border-gray-700 text-gray-400 hover:text-white' : 'border-gray-200 text-gray-600 hover:bg-gray-50')
        }`}
      >
        {sending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Mail className={`h-4 w-4 ${sent ? 'fill-current' : ''}`} />
        )}
        <span>{sent ? 'Sent' : 'Send Mail'}</span>
      </Button>

      {showConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-all duration-300">
          <div className={`w-full max-w-md rounded-2xl shadow-2xl p-6 transform transition-all duration-300 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-100'}`}>
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-yellow-100 text-yellow-600 mx-auto mb-4">
              <Mail className="h-6 w-6" />
            </div>
            <h3 className={`text-xl font-bold text-center mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Email Already Sent</h3>
            <p className={`text-center mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              An order confirmation email has already been sent for order #{orderId}. Are you sure you want to send it again?
            </p>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="flex-1 bg-green-600 hover:bg-green-700 border-green-600 text-white"
                onClick={() => handleSendMail(true)}
              >
                Yes, Send Again
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SendMailButton;
